import { domToCanvas } from 'modern-screenshot';

import type { Rect } from '../types';

function pageBackground(): string {
  for (const el of [document.body, document.documentElement]) {
    const color = getComputedStyle(el).backgroundColor;
    if (color && color !== 'transparent' && color !== 'rgba(0, 0, 0, 0)') return color;
  }
  return '#ffffff';
}

/**
 * Renders the visible viewport to a PNG data URL and marks `highlight` with a red frame.
 *
 * The DOM is redrawn rather than grabbed from the screen, so no permission prompt appears.
 * The trade-offs: cross-origin iframes, <video>, canvas content from other origins and some CSS
 * effects can be missing, and content inside an inner scroll container shows at its top.
 */
export async function captureViewport(options: {
  exclude: Element;
  highlight?: Rect;
  /** Viewport width taken by the docked sidebar, left out of the screenshot. */
  reserved?: { side: 'right' | 'left'; width: number };
}): Promise<string> {
  const body = document.body;
  const bodyRect = body.getBoundingClientRect();
  const background = pageBackground();

  const rendered = await domToCanvas(body, {
    backgroundColor: background,
    scale: Math.min(window.devicePixelRatio || 1, 2),
    // <noscript> is invisible when JavaScript runs, but the renderer would draw its text and shift the page.
    filter: (node) => node !== options.exclude && node.nodeName !== 'NOSCRIPT',
    // The clone is rendered inside a fresh document whose default body margin would shift the
    // content by 8px. Its real offset is already in bodyRect, so the clone gets no margin.
    style: { margin: '0' },
  });

  const ratio = rendered.width / Math.max(1, bodyRect.width);
  const reservedWidth = options.reserved?.width ?? 0;
  // With the sidebar on the left, shift everything so x=0 is the page's own left edge.
  const offsetX = options.reserved?.side === 'left' ? reservedWidth : 0;
  const out = document.createElement('canvas');
  out.width = Math.round((document.documentElement.clientWidth - reservedWidth) * ratio);
  out.height = Math.round(window.innerHeight * ratio);
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  ctx.fillStyle = background;
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(rendered, (bodyRect.left - offsetX) * ratio, bodyRect.top * ratio);

  if (options.highlight) {
    const pad = 4;
    const { x, y, width, height } = options.highlight;
    ctx.strokeStyle = '#e1491b';
    ctx.lineWidth = 3 * ratio;
    ctx.strokeRect(
      (x - offsetX - pad) * ratio,
      (y - pad) * ratio,
      (width + pad * 2) * ratio,
      (height + pad * 2) * ratio,
    );
  }

  return out.toDataURL('image/png');
}
